import React, { useState, useEffect } from 'react';
import WizardStep from './WizardStep';
import api from '../../utils/api';
import { Settings, Users, Briefcase, CheckCircle, Shield, ArrowRight } from 'lucide-react';
import { toast } from 'react-hot-toast';

const SetupWizard = ({ isOpen, onClose, onComplete }) => {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);

    // Data State
    const [availableTemplates, setAvailableTemplates] = useState([]);
    const [selectedTemplates, setSelectedTemplates] = useState([]);

    const [teams, setTeams] = useState([
        { name: 'Engineering', lead: '' },
        { name: 'Sales', lead: '' },
        { name: 'Marketing', lead: '' },
        { name: 'HR', lead: '' }
    ]);

    // Fetch Templates on mount
    useEffect(() => {
        if (isOpen) {
            fetchRoleTemplates();
        }
    }, [isOpen]);

    const fetchRoleTemplates = async () => {
        try {
            const response = await api.get('/employees/roles/templates');
            setAvailableTemplates(response.data.templates || []);
        } catch (error) {
            console.error('Failed to fetch templates', error);
        }
    };

    const handleNext = () => setStep(s => s + 1);
    const handleBack = () => setStep(s => s - 1);

    const toggleTemplate = (template) => {
        setSelectedTemplates(prev => {
            const exists = prev.find(t => t.id === template.id);
            if (exists) return prev.filter(t => t.id !== template.id);
            return [...prev, template];
        });
    };

    const handleAddTeam = () => {
        setTeams([...teams, { name: '', lead: '' }]);
    };

    const updateTeam = (index, field, value) => {
        const newTeams = [...teams];
        newTeams[index][field] = value;
        setTeams(newTeams);
    };

    const removeTeam = (index) => {
        setTeams(teams.filter((_, i) => i !== index));
    };

    const handleFinish = async () => {
        setLoading(true);
        try {
            // 1. Create Selected Roles
            for (const template of selectedTemplates) {
                await api.post('/employees/roles', {
                    role_name: template.role_name,
                    description: template.description,
                    permissions: template.permissions
                });
            }

            // 2. Create Teams
            for (const team of teams) {
                if (team.name.trim()) {
                    await api.post('/teams', {
                        team_name: team.name,
                        // description: 'Created during setup' 
                    });
                }
            }

            toast.success('Setup completed successfully! 🎉');
            onComplete && onComplete();
            onClose();
        } catch (error) {
            console.error('Setup failed', error);
            toast.error('Failed to complete setup. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">

            {/* Step 1: Welcome */}
            {step === 1 && (
                <WizardStep
                    currentStep={1}
                    totalSteps={4}
                    title="Welcome to envion"
                    subtitle="Let's get your workspace ready in less than 2 minutes."
                    onNext={handleNext}
                    nextLabel="Start Setup"
                    onBack={onClose} // Allow exit on first step? Or force? Let's allow exit.
                    backLabel="Skip for Now"
                >
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                        <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-6 text-primary">
                            <Settings size={40} />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Configure Your Organization</h3>
                        <p className="text-gray-500 max-w-md mx-auto mb-8">
                            We'll help you set up essential roles, permissions, and teams so you can start inviting employees right away.
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full max-w-2xl">
                            <div className="p-4 border border-gray-100 rounded-xl bg-gray-50 text-left">
                                <div className="flex items-center gap-2 mb-2">
                                    <Shield className="text-primary" size={20} />
                                    <h4 className="font-semibold text-gray-900">Define Roles</h4>
                                </div>
                                <p className="text-sm text-gray-500">Choose from standard industry roles or create your own later.</p>
                            </div>
                            <div className="p-4 border border-gray-100 rounded-xl bg-gray-50 text-left">
                                <div className="flex items-center gap-2 mb-2">
                                    <Users className="text-primary" size={20} />
                                    <h4 className="font-semibold text-gray-900">Create Teams</h4>
                                </div>
                                <p className="text-sm text-gray-500">Set up your department structure and hierarchy.</p>
                            </div>
                        </div>
                    </div>
                </WizardStep>
            )}

            {/* Step 2: Role Templates */}
            {step === 2 && (
                <WizardStep
                    currentStep={2}
                    totalSteps={4}
                    title="Select Role Templates"
                    subtitle="Select the roles you want to add to your organization. You can edit permissions later."
                    onNext={handleNext}
                    onBack={handleBack}
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {availableTemplates.map((template) => {
                            const isSelected = selectedTemplates.find(t => t.id === template.id);
                            return (
                                <div
                                    key={template.id}
                                    onClick={() => toggleTemplate(template)}
                                    className={`
                                        p-4 rounded-xl border-2 transition-all cursor-pointer relative
                                        ${isSelected ? 'border-primary bg-primary/5' : 'border-gray-100 hover:border-primary/50 bg-white'}
                                    `}
                                >
                                    {isSelected && (
                                        <div className="absolute top-3 right-3 text-primary">
                                            <CheckCircle size={20} fill="currentColor" className="text-white" />
                                        </div>
                                    )}
                                    <h4 className="font-semibold text-gray-900 mb-1">{template.role_name}</h4>
                                    <p className="text-sm text-gray-500 mb-3">{template.description}</p>
                                    <span className="inline-block px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-lg font-medium">
                                        {template.category || 'General'}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </WizardStep>
            )}

            {/* Step 3: Teams Setup */}
            {step === 3 && (
                <WizardStep
                    currentStep={3}
                    totalSteps={4}
                    title="Create Your Teams"
                    subtitle="Add the departments or teams in your organization. You can add people to them later."
                    onNext={handleFinish}
                    nextLabel={loading ? 'Setting up...' : 'Finish Setup'}
                    isNextDisabled={loading}
                    onBack={handleBack}
                >
                    <div className="space-y-4">
                        {teams.map((team, index) => (
                            <div key={index} className="flex items-center gap-3">
                                <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-full text-gray-500 font-medium text-sm">
                                    {index + 1}
                                </div>
                                <input
                                    type="text"
                                    placeholder="Team Name (e.g. Engineering)"
                                    value={team.name}
                                    onChange={(e) => updateTeam(index, 'name', e.target.value)}
                                    className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                                />
                                <button
                                    onClick={() => removeTeam(index)}
                                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                >
                                    <Users size={18} />
                                </button>
                            </div>
                        ))}

                        <button
                            onClick={handleAddTeam}
                            className="ml-11 text-sm font-medium text-primary hover:text-primary-hover flex items-center gap-1"
                        >
                            + Add another team
                        </button>
                    </div>
                </WizardStep>
            )}

            {/* Step 4: Completion (Optional, handled by Finish but could show success screen) */}
            {step === 4 && (
                <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
                    <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle size={32} />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">All Set!</h2>
                    <p className="text-gray-500 mb-6">Your organization structure has been created. Now you're ready to invite your first employees.</p>
                    <button
                        onClick={() => {
                            onComplete && onComplete();
                            onClose();
                        }}
                        className="w-full py-3 bg-primary text-white rounded-xl font-semibold hover:bg-primary-hover transition-colors"
                    >
                        Go to Dashboard
                    </button>
                </div>
            )}
        </div>
    );
};

export default SetupWizard;
